import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { axiosInstance } from "../utils/axios"; // customize this path
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import Button from "./Button";
import useDialogStore from "../stores/useDialogStore";
import { useQueryClient } from "@tanstack/react-query";

const schema = Joi.object({
  client_id: Joi.string().pattern(/^\d+$/).required().messages({
    "string.base": "Client ID must be a string.",
    "string.pattern.base": "Client ID must contain only numbers.",
    "any.required": "Client ID is required.",
  }),
  client_name: Joi.string().when("client_id", {
    is: Joi.exist(),
    then: Joi.optional(),
    otherwise: Joi.required().messages({
      "string.base": "Client name must be a string.",
      "any.required": "Client name is required when client ID is not provided.",
    }),
  }),
  unique_ids: Joi.string().required().messages({
    "string.base": "Unique IDs must be a string.",
    "any.required": "Unique IDs are required.",
  }),
  invoice_number: Joi.string().optional().allow("", null).messages({
    "string.base": "Invoice number must be a string.",
  }),
  reserved_date: Joi.date().required().messages({
    "date.base": "Reserved date must be a valid date.",
    "any.required": "Reserved date is required.",
  }),
});

export default function ReserveMultipleDevices() {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,

    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: {
      reserved_date: new Date().toISOString().split("T")[0], // Set default to today's date
    },
  });
  const { closeDialog } = useDialogStore();
  const [query, setQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showNewClientFields, setShowNewClientFields] = useState(false);
  const queryClient = useQueryClient();
  // Watch the client_id field for changes

  // Handle client ID input change
  const handleClientIdChange = (e) => {
    let value = e.target.value;
    // Remove any non-numeric characters
    value = value.replace(/[^0-9]/g, "");
    setValue("client_id", value.toString());
  };

  const mutation = useMutation({
    mutationFn: async (formData) => {
      return await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-statuses/multiple-reserve-devices`,
        formData
      );
    },
    onSuccess: () => {
      toast.success("Reserved successfully!");
    
      queryClient.invalidateQueries({ queryKey: ["not-reserved-devices"] });
      queryClient.invalidateQueries({ queryKey: ["reserved-devices"] });
      queryClient.invalidateQueries({ queryKey: ["sold-devices"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceDetails"] });
      closeDialog();
    },
    onError: (error) => {
      toast.error(error.response.data.error);
    },
  });

  useEffect(() => {
    let timeoutId;
    setShowLoading(true);

    const fetchClients = async () => {
      try {
        const response = await axiosInstance.post(
          `${window.REACT_APP_API_ENDPOINT}/users/fetch-users`,
          query.trim() !== "" ? { q: query } : {}
        );
        setClients(response.data.data);
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setShowLoading(false);
      }
    };

    timeoutId = setTimeout(fetchClients, 1500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [query]);

  const onSubmit = (data) => {
    const uniqueIds = data.unique_ids
      .split("\n")
      .map((id) => id.trim())
      .filter((id) => id !== "");

    // Check if required fields are filled
    if (!data.client_id || uniqueIds.length === 0) {
      toast.error("Please fill in all required fields.");
      return;
    }

    mutation.mutate({
      client_id: `A${data.client_id}`,
      client_name: data.client_name,
      invoice_number: data.invoice_number || null,
      unique_ids: uniqueIds,
      reserved_date: data.reserved_date,
    });
  };

  return (
    <form
      className="max-w-lg mx-auto bg-white p-6  rounded-lg space-y-4"
      onSubmit={handleSubmit(onSubmit)} // ✅ here
    >
      <div>
        <label className="block font-medium">
          Client <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <Combobox
            as="div"
            onChange={(client) => {
              setValue("client_id", client.id.toString());
              setValue("client_name", client.name);
            }}
            className="w-full"
          >
            <div className="relative">
              <Combobox.Input
                className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                onChange={(event) => setQuery(event.target.value)}
                displayValue={(client) => client?.name || ""}
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
              {showLoading && query.trim() !== "" && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <Combobox.Option
                    key="loading"
                    className="relative cursor-default select-none py-2 pl-3 pr-9 bg-indigo-600 text-white"
                  >
                    <span className="block truncate">Loading...</span>
                  </Combobox.Option>
                </Combobox.Options>
              )}
              {clients.length > 0 && !showLoading && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {clients.map((client) => (
                    <Combobox.Option
                      key={client.id}
                      value={client}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-3 pr-9 ${
                          active ? "bg-indigo-600 text-white" : "text-gray-900"
                        }`
                      }
                    >
                      {({ active, selected }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? "font-semibold" : ""
                            }`}
                          >
                            {client.name}
                          </span>
                          {selected && (
                            <span
                              className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                active ? "text-white" : "text-indigo-600"
                              }`}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}
              {clients.length === 0 && query.trim() !== "" && !showLoading && (
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <Combobox.Option
                    key="no-user"
                    className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900"
                    disabled
                  >
                    <span className="block truncate">No user found</span>
                  </Combobox.Option>
                </Combobox.Options>
              )}
            </div>
          </Combobox>
          <button
            className={`${
              showNewClientFields ? "bg-red-500" : "bg-green-500"
            } text-white w-9 h-9 rounded-md flex items-center justify-center`}
            type="button"
            onClick={() => setShowNewClientFields(!showNewClientFields)}
          >
            {!showNewClientFields ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        </div>
        {showNewClientFields && (
          <div className="mt-2 space-y-2">
            <div>
              <label className="block font-medium">
                New Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register("client_name")}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
              />
              {errors.client_name && (
                <p className="text-red-500 text-sm">
                  {errors.client_name.message}
                </p>
              )}
            </div>
            <div>
              <label className="block font-medium">
                New Client ID <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <span className="text-gray-500">A</span>
                </div>
                <input
                  type="text"
                  {...register("client_id")}
                  onChange={handleClientIdChange}
                  className="w-full border border-gray-300 pl-8 px-3 py-2 rounded-md"
                  placeholder="Enter numbers"
                />
              </div>
              {errors.client_id && (
                <p className="text-red-500 text-sm">
                  {errors.client_id.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Numbers only (A will be added automatically)
              </p>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block font-medium">
          Unique IDs (one per line) <span className="text-red-500">*</span>
        </label>
        <textarea
          {...register("unique_ids")}
          rows={6}
          placeholder={`00001\n00002\n00003`}
          className="w-full border border-gray-300 px-3 py-2 rounded-md"
        />
        {errors.unique_ids && (
          <p className="text-red-500 text-sm">{errors.unique_ids.message}</p>
        )}
      </div>

      {/* <div>
        <label className="block font-medium">
          Invoice Number
        </label>
        <input
          type="text"
          {...register("invoice_number")}
          placeholder="Leave empty to auto-generate"
          className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
        />
        {errors.invoice_number && (
          <p className="text-red-500 text-sm">
            {errors.invoice_number.message}
          </p>
        )}
      </div> */}

      <div>
        <label className="block font-medium">Reserved Date</label>
        <input
          type="date"
          {...register("reserved_date")}
          className="w-full border border-gray-300 px-3 py-1.5 rounded-md"
        />
        {errors.reserved_date && (
          <p className="text-red-500 text-sm">{errors.reserved_date.message}</p>
        )}
      </div>

      <Button
        type="submit"
        className="bg-bluePrimary text-white px-4 py-2 rounded-md"
        isLoading={mutation.isLoading}
      >
        {mutation.isLoading ? "Submitting..." : "Submit Reservation"}
      </Button>
    </form>
  );
}